import CirclePattern from "./CirclePattern";

const SOLAR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/96197346-3905-4338-9f6f-545a4a9da490/images/10cc1c94684b004e00c532d47ed0b78ec62bc97753898427adb3f90dda938bb1.png";

const FLOW_STEPS = [
  {
    n: "01",
    title: "Solar PV",
    text: "Captures renewable energy during daylight hours.",
  },
  {
    n: "02",
    title: "Battery storage",
    text: "Stores excess solar energy for later use, or charges during off-peak periods.",
    optional: true,
  },
  {
    n: "03",
    title: "ecoHeatwave radiant system",
    text: "Uses stored or direct electricity efficiently to deliver invisible, zoned heating across the building.",
  },
  {
    n: "04",
    title: "Air source heat pump hot water cylinder",
    text: "Provides domestic hot water as part of a fully compliant low-carbon system.",
  },
];

const BENEFITS = [
  "Maximises self-consumption of solar energy",
  "Reduces reliance on peak grid electricity",
  "Enables off-peak energy optimisation with storage",
  "Supports zoned, demand-led heating control",
  "Integrates with compliant heat pump hot water systems",
  "Improves long-term operational efficiency",
];

export default function EnergyAndHotWater() {
  return (
    <>
      <EnergyHeader />
      <FlowOverview />
      <PracticeSection />
      <IntegrationMatters />
      <SystemBenefits />
      <ClosingBanner />
    </>
  );
}

function EnergyHeader() {
  return (
    <section id="energy" data-testid="energy-section" className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="overline mb-6">Energy</div>
            <h2
              data-testid="energy-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-ecoh-ink"
            >
              ecoHeatwave Energy Ecosystem.
            </h2>
            <p className="mt-6 text-ecoh-ink/70 text-lg md:text-xl font-light leading-snug max-w-xl">
              A fully integrated approach to low-carbon building energy.
            </p>
            <p className="mt-10 max-w-xl text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
              ecoHeatwave is designed to operate as part of a complete,
              connected building energy system — working seamlessly with solar
              generation, battery storage, and modern heat pump technology to
              maximise efficiency and reduce running costs.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-ecoh-surface ring-1 ring-ecoh-ink/8">
              <img
                src={SOLAR_IMG}
                alt="Contemporary UK new-build house with solar PV on the roof"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FlowStepCard({ step }) {
  return (
    <div
      data-testid={`flow-step-${step.n}`}
      className="bg-ecoh-surface px-7 py-7 md:py-8 flex flex-col"
    >
      <div className="flex items-baseline justify-between">
        <div className="text-xs tracking-[0.22em] uppercase text-ecoh-accent">
          {step.n}
        </div>
        {step.optional && (
          <div className="text-[10px] tracking-[0.22em] uppercase text-ecoh-muted">
            Optional
          </div>
        )}
      </div>
      <h4 className="mt-4 font-display text-lg md:text-xl font-light tracking-tight leading-snug text-ecoh-ink">
        {step.title}
      </h4>
      <p className="mt-3 text-ecoh-ink/70 text-sm font-light leading-relaxed">
        {step.text}
      </p>
    </div>
  );
}

function FlowOverview() {
  return (
    <div className="bg-ecoh-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <h3 className="font-display font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink whitespace-nowrap text-[clamp(1.25rem,4.2vw,3rem)]">
          Generation. Storage. Heating. Hot water.
        </h3>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ecoh-ink/10 rounded-2xl overflow-hidden">
          {FLOW_STEPS.map((s) => (
            <FlowStepCard key={s.n} step={s} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PracticeSection() {
  return (
    <section data-testid="energy-practice" className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              How the system works in practice.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
            <p>
              During the day, solar PV generates electricity that can be used
              immediately within the building, diverted to the hot water
              cylinder or stored in a battery system for later use. Rather
              than exporting surplus energy, the system prioritises
              self-consumption, increasing overall efficiency.
            </p>
            <p>
              As energy demand shifts into the evening or periods of occupancy,
              stored electricity from the battery can be used to power
              ecoHeatwave&apos;s zoned radiant heating system, delivering
              targeted comfort exactly where and when it is needed.
            </p>
            <p>
              Where required, an air source heat pump hot water cylinder
              provides efficient hot water generation, ensuring the system
              remains fully aligned with modern low-carbon building standards.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function IntegrationMatters() {
  return (
    <div className="bg-ecoh-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              One coordinated system. Less waste, more control.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-ink/80 text-base md:text-lg font-light leading-relaxed">
            <p>
              By combining generation, storage, and intelligent consumption
              into a single coordinated system, ecoHeatwave helps reduce
              energy waste, improve operational efficiency, and enhance control
              over building performance.
            </p>
            <p>
              As battery storage costs continue to reduce, fully integrated
              systems that combine heating, hot water, solar PV, and storage
              are becoming increasingly viable.
            </p>
            <p className="font-semibold text-ecoh-ink">
              A fully integrated ecoHeatwave system, paired with battery
              storage, typically costs less to install than a standalone
              ASHP — and far less to run and service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemBenefits() {
  return (
    <section data-testid="energy-benefits" className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
            System benefits.
          </h3>
        </div>
        <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-6">
          {BENEFITS.map((b) => (
            <li
              key={b}
              data-testid={`energy-benefit-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-start gap-4 border-t border-ecoh-ink/10 pt-5"
            >
              <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-ecoh-accent shrink-0" />
              <span className="text-ecoh-ink/85 text-base md:text-lg font-light leading-relaxed">
                {b}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ClosingBanner() {
  return (
    <div className="relative bg-ecoh-ink text-white overflow-hidden">
      <CirclePattern
        className="pointer-events-none select-none absolute inset-0 w-full h-full"
        opacity={0.5}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ecoh-ink via-ecoh-ink/80 to-ecoh-ink/40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="max-w-3xl">
          <p className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.15] text-white">
            Generation, storage, heating and hot water — designed to work as
            one low-carbon system.
          </p>
        </div>
      </div>
    </div>
  );
}
